import { ORDER_TYPE } from '@exam-management/common/utils';
import {
  FilterOperator,
  QSetAuditLogIdentifierType,
  useSearchQSetAuditLogsQuery,
} from '@exam-management/graphql';
import { useParams } from '@exam-management/router';
import { DataGrid, type GridSortModel, InfoOutlinedIcon, NoRowsOverlay } from '@shared/components';

import { useQSetActivityLogSearchParams } from '../../hooks';

import { columns } from './column';

export default function QSetActivityLogList() {
  const { qSetId } = useParams('/exam-courses/:courseId/qsets/:qSetId/activity-log');
  const [{ page, pageSize, sort, field }, setQSetActivityLogSearchParams] =
    useQSetActivityLogSearchParams();

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? 'actionTime';
    const sort = sorting[0]?.sort ?? 'desc';
    setQSetActivityLogSearchParams({ sort: sort, field: field });
  };

  const { data, loading } = useSearchQSetAuditLogsQuery({
    variables: {
      searchQSetAuditLogInput: {
        order: {
          field,
          type: ORDER_TYPE[sort],
        },
        filter: {
          conditions: [
            {
              field: 'executionResult',
              operator: FilterOperator.Equal,
              value: 'SUCCESS',
            },
          ],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        jsonContainsFilter: {
          field: 'identifiers',
          value: {
            identifierId: qSetId,
            identifierType: QSetAuditLogIdentifierType.QsetId,
          },
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const total = data?.excSearchQSetAuditLog.total ?? 0;
  const isSearchResultEmpty = total === 0;

  return (
    <DataGrid
      rows={data?.excSearchQSetAuditLog.qSetAuditLogs || []}
      columns={columns}
      getRowHeight={() => 'auto'}
      columnHeaderHeight={48}
      disableColumnMenu
      sortingMode='server'
      onSortModelChange={handleSortModelChange}
      loading={loading}
      slots={{
        noRowsOverlay: () =>
          !isSearchResultEmpty ? (
            <NoRowsOverlay icon={InfoOutlinedIcon} title='ยังไม่มีข้อมูล Audit Logs' />
          ) : (
            <NoRowsOverlay />
          ),
      }}
      slotProps={{
        footer: {
          total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_, newPage: number) => {
              setQSetActivityLogSearchParams({ page: newPage });
            },
            onRowsPerPageChange: (event) => {
              const newPageSize = parseInt(event.target.value, 10);
              setQSetActivityLogSearchParams({ page: 0, pageSize: newPageSize });
            },
          },
        },
      }}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(2),
        border: `1px solid ${theme.palette.grey[300]}`,
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiDataGrid-row .MuiDataGrid-cell': {
          padding: theme.spacing(3),
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          ...theme.typography.body2,
        },
        '& .MuiDataGrid-columnHeader': {
          px: 3,
        },
        '--DataGrid-overlayHeight': '400px',
      })}
    />
  );
}
